// Web-side mirror of the API's Zod-inferred shapes. Kept hand-rolled so the
// web app doesn't need to import server code; the route boundary parses
// responses with Zod schemas (see `lib/api.ts`'s optional `schema` arg) when
// the contract matters. Update both sides when the API shape changes.

import { z } from 'zod';

// ---- API error envelope ----
// Mirrors `apps/protocol-api/src/lib/envelope.ts` exactly.
export const ApiErrorKindSchema = z.enum([
  'auth',
  'forbidden_origin',
  'cors_not_configured',
  'validation_error',
  'not_found',
  'default',
]);
export type ApiErrorKind = z.infer<typeof ApiErrorKindSchema>;

export const ApiErrorEnvelopeSchema = z
  .object({
    error: z.string(),
    detail: z.string().optional(),
    code: z.union([z.string(), z.number()]).optional(),
    fields: z
      .array(z.object({ path: z.string(), message: z.string() }))
      .optional(),
  })
  .passthrough();
export type ApiErrorEnvelope = z.infer<typeof ApiErrorEnvelopeSchema>;

// ---- whoami ----
export const WhoamiResponseSchema = z.object({
  ok: z.literal(true),
  fingerprint: z.string(),
});
export type WhoamiResponse = z.infer<typeof WhoamiResponseSchema>;

// ---- healthz ----
export const HealthzResponseSchema = z.object({
  ok: z.literal(true),
});
export type HealthzResponse = z.infer<typeof HealthzResponseSchema>;

// ---- equipment enum ----
// Mirrors EQUIPMENT_VALUES in apps/protocol-api/src/schemas/profile.ts.
// Adding to this enum is a coordinated change across server + client.
export const EQUIPMENT_VALUES = [
  'speediance',
  'outdoor_run',
  'treadmill',
  'outdoor_padel',
  'rowing',
  'bodyweight',
] as const;
export const EquipmentSchema = z.enum(EQUIPMENT_VALUES);
export type Equipment = z.infer<typeof EquipmentSchema>;

// Plan 4 follow-up (I2) — recurring per-weekday equipment override.
// Mirrors apps/protocol-api/src/schemas/profile.ts weekdayEquipmentSchema.
export const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;
export type Weekday = (typeof WEEKDAYS)[number];
export const WeekdayEquipmentSchema = z.object({
  Mon: z.array(EquipmentSchema).nullable(),
  Tue: z.array(EquipmentSchema).nullable(),
  Wed: z.array(EquipmentSchema).nullable(),
  Thu: z.array(EquipmentSchema).nullable(),
  Fri: z.array(EquipmentSchema).nullable(),
  Sat: z.array(EquipmentSchema).nullable(),
  Sun: z.array(EquipmentSchema).nullable(),
});
export type WeekdayEquipment = z.infer<typeof WeekdayEquipmentSchema>;

// Operator-friendly labels for the closed equipment set. Order matches the
// enum so the UI can render checkboxes deterministically.
export const EQUIPMENT_LABELS: Record<Equipment, string> = {
  speediance: 'Speediance',
  outdoor_run: 'Outdoor run',
  treadmill: 'Treadmill',
  outdoor_padel: 'Outdoor padel',
  rowing: 'Rowing',
  bodyweight: 'Bodyweight',
};

// ---- weekly commitments ----
// Mirrors apps/protocol-api/src/schemas/profile.ts weeklyCommitmentsSchema.
// Every workout_log type EXCEPT `Mixed`: a commitment is the operator's own
// declaration, and `Mixed` only ever originates from the coach.
export const COMMITMENT_TYPES = [
  'Strength',
  'Run-Easy',
  'Run-Long',
  'Run-Tempo',
  'Run-Intervals',
  'Run-Recovery',
  'Padel',
  'Rest',
] as const;
export type CommitmentType = (typeof COMMITMENT_TYPES)[number];
const CommitmentTypeSchema = z.enum(COMMITMENT_TYPES);
export const WeeklyCommitmentsSchema = z.object({
  Mon: CommitmentTypeSchema.nullable(),
  Tue: CommitmentTypeSchema.nullable(),
  Wed: CommitmentTypeSchema.nullable(),
  Thu: CommitmentTypeSchema.nullable(),
  Fri: CommitmentTypeSchema.nullable(),
  Sat: CommitmentTypeSchema.nullable(),
  Sun: CommitmentTypeSchema.nullable(),
});
export type WeeklyCommitments = z.infer<typeof WeeklyCommitmentsSchema>;

// ---- profile (singleton) ----
// Mirrors apps/protocol-api/src/schemas/profile.ts (ProfileResponse).
// All fields nullable so the UI can render a half-populated profile.
export const ProfileResponseSchema = z.object({
  height_cm: z.number().nullable(),
  weight_start_kg: z.number().nullable(),
  goal_weight_kg: z.number().nullable(),
  age: z.number().nullable(),
  start_date: z.string().nullable(),
  // Operator-declared standing claim on a weekday: the coach plans nothing
  // else there, and a date with no workout_log row falls back to its
  // weekday's commitment. Whole field null = nothing committed.
  weekly_commitments: WeeklyCommitmentsSchema.nullable(),
  // 0011 — operator-rails-and-override plan (U1).
  // Both arrays serialized as JSON text on the server; the route returns
  // the parsed shape (or null when unset).
  days_available: z.array(z.boolean()).length(7).nullable(),
  equipment_available: z.array(EquipmentSchema).nullable(),
  time_available_minutes: z.number().int().positive().nullable(),
  // Legacy/deprecated 5K target (kept for back-compat, no longer the UI source).
  target_5k_seconds: z.number().int().positive().nullable(),
  // Generic run goal (half-marathon reframe): distance (m) + target time (s).
  target_run_distance_m: z.number().positive().nullable(),
  target_run_time_seconds: z.number().int().positive().nullable(),
  // Plan 3.5 U13 — v2 coach bundle's `goals.strengthFocus`. Treated as
  // 'build' when null. Operator-settable via Settings.
  strength_focus: z.enum(['maintain', 'build', 'maximize']).nullable(),
  // Plan 4 follow-up (I2) — recurring per-weekday equipment override.
  // Mon..Sun keyed; each value an equipment array (override for that
  // weekday, every week) or null (inherit equipment_available). The
  // whole field null = no recurring overrides declared.
  weekday_equipment: WeekdayEquipmentSchema.nullable().default(null),
  goal_cal: z.number().nullable(),
  goal_protein_g: z.number().nullable(),
  goal_fat_g: z.number().nullable(),
  goal_carb_g: z.number().nullable(),
  updated_at: z.number(),
});
export type ProfileResponse = z.infer<typeof ProfileResponseSchema>;

// Defaults for the UI when GET /api/profile returns 404 (uninitialized).
// Match the original `DEFAULT_PROFILE` shape in protocol.html so the Today
// tab can render without a setup wizard. The Settings tab (U8) is the only
// place these can be edited.
export const DEFAULT_PROFILE: ProfileResponse = {
  height_cm: 182,
  weight_start_kg: 95,
  goal_weight_kg: 82,
  age: 45,
  start_date: null,
  weekly_commitments: null,
  // Constraints + 5K goal default to null — uninitialized until the
  // operator fills them in through Settings → Constraints / Profile.
  days_available: null,
  equipment_available: null,
  time_available_minutes: null,
  target_5k_seconds: null,
  target_run_distance_m: null,
  target_run_time_seconds: null,
  strength_focus: null,
  weekday_equipment: null,
  goal_cal: 2300,
  goal_protein_g: 200,
  goal_fat_g: 70,
  goal_carb_g: 220,
  updated_at: 0,
};

// ---- constraint windows (U2 of operator-rails-and-override plan) ----
//
// Mirrors apps/protocol-api/src/schemas/constraint-window.ts. Each window
// is a date-range override for the operator's base constraints (which live
// on the singleton `profile` row). Each override field is independently
// nullable so the operator can declare "only equipment changes on vacation"
// without re-stating days and time.
//
// On the wire, `null` and `undefined` carry different meanings on PUT:
//   - field omitted (`undefined`) → preserve the existing column value
//   - explicit `null`             → clear the override (fall through to base)
//
// On responses, the server already parses the JSON-text columns and returns
// arrays (or null) for `days_available` / `equipment_available`.

const isoDateRegex = /^\d{4}-\d{2}-\d{2}$/;

export const ConstraintWindowRowSchema = z.object({
  id: z.number().int(),
  start_date: z.string().regex(isoDateRegex),
  end_date: z.string().regex(isoDateRegex),
  days_available: z.array(z.boolean()).length(7).nullable(),
  equipment_available: z.array(EquipmentSchema).nullable(),
  time_available_minutes: z.number().int().positive().nullable(),
  label: z.string().nullable(),
  updated_at: z.number(),
});
export type ConstraintWindowRow = z.infer<typeof ConstraintWindowRowSchema>;
export const ConstraintWindowRowsSchema = z.array(ConstraintWindowRowSchema);

// Create body — start/end required; override fields optional + nullable.
export const ConstraintWindowCreateSchema = z.object({
  start_date: z.string().regex(isoDateRegex),
  end_date: z.string().regex(isoDateRegex),
  days_available: z.array(z.boolean()).length(7).nullish(),
  equipment_available: z.array(EquipmentSchema).nullish(),
  time_available_minutes: z.number().int().positive().nullish(),
  label: z.string().max(200).nullish(),
});
export type ConstraintWindowCreate = z.infer<typeof ConstraintWindowCreateSchema>;

// Update body — all fields optional; semantics per the null-vs-undefined
// rule above.
export const ConstraintWindowUpdateSchema = z.object({
  start_date: z.string().regex(isoDateRegex).optional(),
  end_date: z.string().regex(isoDateRegex).optional(),
  days_available: z.array(z.boolean()).length(7).nullish(),
  equipment_available: z.array(EquipmentSchema).nullish(),
  time_available_minutes: z.number().int().positive().nullish(),
  label: z.string().max(200).nullish(),
});
export type ConstraintWindowUpdate = z.infer<typeof ConstraintWindowUpdateSchema>;

// ---- daily targets (singleton, U8) ----
//
// Mirrors apps/protocol-api/src/schemas/daily-targets.ts. The route always
// returns the four targets (defaulted to CLAUDE.md values when D1 is empty)
// so the UI doesn't have to branch on first-run.
export const DailyTargetsResponseSchema = z.object({
  goal_cal: z.number().int().positive(),
  goal_protein_g: z.number().nonnegative(),
  goal_fat_g: z.number().nonnegative(),
  goal_carb_g: z.number().nonnegative(),
  updated_at: z.number(),
});
export type DailyTargetsResponse = z.infer<typeof DailyTargetsResponseSchema>;

// ---- weight log row ----
export const WeightRowSchema = z.object({
  date: z.string(),
  weight_kg: z.number(),
  updated_at: z.number(),
});
export type WeightRow = z.infer<typeof WeightRowSchema>;
export const WeightRowsSchema = z.array(WeightRowSchema);

// 0029 — operator's subjective Day-surface comments that feed the coach.
export const SurfaceTypeSchema = z.enum(['activity', 'macros', 'weight']);
export type SurfaceType = z.infer<typeof SurfaceTypeSchema>;
export const SurfaceCommentRowSchema = z.object({
  id: z.number(),
  date: z.string(),
  surface_type: SurfaceTypeSchema,
  text: z.string(),
  created_at: z.number(),
  updated_at: z.number(),
});
export type SurfaceCommentRow = z.infer<typeof SurfaceCommentRowSchema>;
export const SurfaceCommentRowsSchema = z.array(SurfaceCommentRowSchema);

// ---- daily log row ----
// Added in API migration 0005: fat_g, carb_g, meals_eaten. Fat / carb mirror
// the protein_g shape (nullable real). `meals_eaten` carries per-slot
// payloads so the chip state AND the actual macros eaten sync across
// devices. Per-slot:
//   - `MealLog` (object): the operator logged this meal with these macros
//   - `true`: legacy — pre-edit-support rows. Treat as "use anchor defaults".
//   - `false` | `null` | omitted: not logged.
export const MealLogSchema = z
  .object({
    cal: z.number().nonnegative(),
    p: z.number().nonnegative(),
    f: z.number().nonnegative(),
    c: z.number().nonnegative(),
  })
  .strict();
export type MealLog = z.infer<typeof MealLogSchema>;

export const MealSlotValueSchema = z.union([
  MealLogSchema,
  z.boolean(),
  z.null(),
]);
export type MealSlotValue = z.infer<typeof MealSlotValueSchema>;

export const MealsEatenSchema = z
  .object({
    lunch: MealSlotValueSchema.optional(),
    snack: MealSlotValueSchema.optional(),
    dinner: MealSlotValueSchema.optional(),
  })
  .nullable();
export type MealsEatenMap = NonNullable<z.infer<typeof MealsEatenSchema>>;

export const DailyRowSchema = z.object({
  date: z.string(),
  calories: z.number().nullable(),
  protein_g: z.number().nullable(),
  fat_g: z.number().nullable(),
  carb_g: z.number().nullable(),
  meals_eaten: MealsEatenSchema,
  updated_at: z.number(),
});
export type DailyRow = z.infer<typeof DailyRowSchema>;
export const DailyRowsSchema = z.array(DailyRowSchema);

// ---- workout log row ----
// Migration 0006 added `exercises_done` — a map of per-exercise completion
// flags. The Day timeline lets the operator tap each exercise row to mark it
// done; flags persist on the workout_log row so they sync across devices.
export const WorkoutStatusSchema = z.enum(['completed', 'skipped', 'not_yet']);
export type WorkoutStatus = z.infer<typeof WorkoutStatusSchema>;
// Keys must match the server contract — `X-{idx}`, the exercise's POSITION in
// the day's coach prescription. This is a render/done-flag key only, not an
// exercise identity (loads + actuals key by `g:<group_id>` / name — see
// strength-session.ts). Anything else is dropped at parse.
export const ExercisesDoneSchema = z
  .record(z.string().regex(/^X-\d{1,2}$/), z.boolean())
  .nullable();
export type ExercisesDoneMap = NonNullable<z.infer<typeof ExercisesDoneSchema>>;
// Plan 4 U6 — workout_log rows written by a coach APPLY (source='coach')
// carry an inlined coach prescription resolved server-side via LEFT JOIN to
// coach_suggestion. Manual rows leave this null; the Day-tab branches on its
// presence to render the coach badge + free-form name.
export const CoachPrescriptionSchema = z.object({
  suggestion_id: z.string(),
  kind: z.enum(['training', 'rest']),
  category: z.enum(['strength', 'run', 'mixed', 'other']),
  name: z.string(),
  reason: z.string().nullable(),
});
export type CoachPrescription = z.infer<typeof CoachPrescriptionSchema>;

// 0027 — the coach's FULL prescribed strength session, inlined on the
// workout_log row via LEFT JOIN to daily_strength_prescription (by date).
// Null unless a coach strength detail was APPLY-d for the row's date. It is the
// only source of a strength session's content: the Day view + every strength
// surface render the operator's exact prescribed list (names + per-set
// reps/weights). Shape mirrors apps/protocol-api/src/schemas/workout.ts.
export const CoachStrengthSetSchema = z.object({
  reps: z.number().int().positive().optional(),
  // Timed hold in seconds (isometrics — planks, wall sits). The coach emits
  // exactly one of reps/duration_s per set; the read side stays permissive.
  duration_s: z.number().int().positive().optional(),
  weight_kg: z.number().nonnegative(),
});
export const CoachStrengthExerciseSchema = z.object({
  name: z.string(),
  group_id: z.number().int().optional(),
  speediance_mode: z.number().int().optional(),
  sets: z.array(CoachStrengthSetSchema),
  reason: z.string().optional(),
});
export const CoachStrengthSessionSchema = z.object({
  exercises: z.array(CoachStrengthExerciseSchema),
});
export type CoachStrengthSet = z.infer<typeof CoachStrengthSetSchema>;
export type CoachStrengthExercise = z.infer<typeof CoachStrengthExerciseSchema>;
export type CoachStrengthSession = z.infer<typeof CoachStrengthSessionSchema>;

export const WorkoutRowSchema = z.object({
  date: z.string(),
  type: z.string(),
  // Row origin: operator-written ('manual' — slot picks, overrides, .fit
  // auto-logs) vs coach APPLY ('coach'). The activity itself is always a
  // concrete `type`; `source` only says who wrote the row.
  source: z.enum(['manual', 'coach']),
  status: WorkoutStatusSchema,
  completed: z.number(),
  exercises_done: ExercisesDoneSchema,
  // Default for backward compat with any cached responses from the
  // pre-U6 server shape; new rows always carry the (possibly null) field.
  coach_prescription: CoachPrescriptionSchema.nullable().default(null),
  // 0027 — populated when a coach strength session was APPLY-d for this date.
  // Default null for backward compat with pre-0027 cached responses.
  coach_strength_session: CoachStrengthSessionSchema.nullable().default(null),
  updated_at: z.number(),
});
export type WorkoutRow = z.infer<typeof WorkoutRowSchema>;
export const WorkoutRowsSchema = z.array(WorkoutRowSchema);

// ---- exercise weight row ----
export const ExerciseWeightRowSchema = z.object({
  exercise_key: z.string(),
  weight_kg: z.number(),
  updated_at: z.number(),
  // True when the progression engine held this target twice in a row
  // without an operator override between holds — surface a "consider
  // deload" hint on the Day cell. Default to false so older servers
  // without the field still parse cleanly.
  deload_flag: z.boolean().default(false),
});
export type ExerciseWeightRow = z.infer<typeof ExerciseWeightRowSchema>;
export const ExerciseWeightRowsSchema = z.array(ExerciseWeightRowSchema);

// ---- Speediance state (U6) ----
//
// Two resources on the Protocol API:
//   - /api/speediance/config   (singleton; GET returns defaults when empty)
//   - /api/speediance/mapping  (array of per-GROUP rows: which catalog
//     variant + unilateral flag to use for a Speediance exercise group)

export const SpeedianceConfigResponseSchema = z.object({
  default_mode: z.number().int(),
  device_type: z.number().int(),
  last_sync_at: z.number().nullable(),
  updated_at: z.number(),
});
export type SpeedianceConfigResponse = z.infer<typeof SpeedianceConfigResponseSchema>;

// One row per Speediance exercise group (`g:<group_id>` is the exercise key
// everywhere else). The row is purely the push-side choice: variant to send,
// and whether the device folds L/R pairs for it.
export const SpeedianceMappingRowSchema = z.object({
  group_id: z.number(),
  variant_id: z.number(),
  group_name: z.string(),
  variant_name: z.string(),
  is_unilateral: z.boolean(),
  updated_at: z.number(),
});
export type SpeedianceMappingRow = z.infer<typeof SpeedianceMappingRowSchema>;
export const SpeedianceMappingRowsSchema = z.array(SpeedianceMappingRowSchema);

// ---- U10: Speediance read-back ----

export const ActualSetRowSchema = z.object({
  id: z.number(),
  date: z.string(),
  // `g:<group_id>` for device-synced sets, the prescribed exercise's name for
  // manual bodyweight actuals.
  exercise_key: z.string(),
  set_idx: z.number(),
  reps_done: z.number().nullable(),
  duration_s: z.number().nullable().default(null),
  weight_done_kg: z.number().nullable(),
  /**
   * Per-rep weight array from Speediance, or null for legacy / manual rows.
   * The headline `weight_done_kg` is the modal value of this array
   * (most-frequent rep weight) — useful when the adaptive autopilot adjusts
   * the load mid-set and the set's reps don't share one weight.
   */
  weights_per_rep: z.array(z.number()).nullable(),
  completed_at: z.number(),
  source: z.enum(['speediance', 'manual']),
  updated_at: z.number(),
  // The target weight the operator was working against at session time.
  // Differs from the current exercise_weights value when the engine has
  // bumped the target since. Default null so older servers without this
  // field still parse — consumers fall back to current weight in that case.
  target_kg_at_session: z.number().nullable().default(null),
});
export type ActualSetRow = z.infer<typeof ActualSetRowSchema>;
export const ActualSetRowsSchema = z.array(ActualSetRowSchema);

export const ManualBodyweightActualsSchema = z.object({
  date: z.string(),
  exercises: z.array(z.object({
    exercise_index: z.number(),
    sets: z.array(z.object({
      set_idx: z.number(),
      reps_done: z.number().optional(),
      duration_s: z.number().optional(),
    })),
  })),
});
export type ManualBodyweightActuals = z.infer<typeof ManualBodyweightActualsSchema>;
export const ManualBodyweightActualsResponseSchema = z.object({
  date: z.string(),
  saved: z.number(),
});
export type ManualBodyweightActualsResponse = z.infer<typeof ManualBodyweightActualsResponseSchema>;

// Per-exercise verdict from the strength progression engine. Mirrors the
// server's `EngineVerdict` discriminated union (see
// apps/protocol-api/src/schemas/speediance.ts). Bumps were applied to
// `exercise_weights` in the same atomic batch as the actuals; holds and
// skips don't mutate state — they're informational so the UI can toast
// "Cable Chest Press held at 25 kg, 4/9 reps at target".
export const EngineVerdictSchema = z.discriminatedUnion('kind', [
  z.object({
    exercise_key: z.string(),
    kind: z.literal('bump'),
    prev_target: z.number(),
    new_target: z.number(),
    ratio: z.number(),
    reps_at_target: z.number(),
    total_reps: z.number(),
  }),
  z.object({
    exercise_key: z.string(),
    kind: z.literal('hold'),
    target: z.number(),
    ratio: z.number(),
    reps_at_target: z.number(),
    total_reps: z.number(),
  }),
  z.object({
    exercise_key: z.string(),
    kind: z.literal('skipped'),
    reason: z.enum(['no_weights_per_rep', 'no_target', 'invalid_target', 'already_processed']),
  }),
]);
export type EngineVerdict = z.infer<typeof EngineVerdictSchema>;

// ---- Cardio .fit ingest (U3/U5) ----
//
// Mirrors apps/protocol-api/src/schemas/fit.ts (FitIngestResponse).
// Server returns this from POST /api/fit/ingest. The PWA's upload card
// reads `ingested` to differentiate fresh ingests from duplicates,
// `parse_status` for parse-failure rendering, and `workout_log_match`
// for the mismatch callout (R15).
export const FitCardioKindSchema = z.enum(['run', 'padel', 'strength', 'other']);
export type FitCardioKind = z.infer<typeof FitCardioKindSchema>;

export const FitIngestResponseSchema = z.object({
  session_id: z.number(),
  content_hash: z.string(),
  kind: FitCardioKindSchema,
  workout_type: z.string().nullable(),
  recorded_at: z.number(),
  parse_status: z.enum(['parsed', 'failed']),
  parse_error: z.string().nullable(),
  workout_log_match: z
    .object({
      // Per CLAUDE.md, workout_log.date is ISO YYYY-MM-DD. The regex
      // pins the format at parse time so a malformed value from the
      // wire surfaces as a Zod validation error rather than corrupting
      // downstream week-bucket math.
      date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
      type: z.string(),
    })
    .nullable(),
  ingested: z.boolean(),
});
export type FitIngestResponse = z.infer<typeof FitIngestResponseSchema>;

// ---- Cardio sessions (U4/U6) ----
//
// Mirrors apps/protocol-api/src/schemas/fit.ts (CardioSessionSummary,
// CardioSessionDetail). The list endpoint (GET /api/fit/sessions?kind=...)
// returns an array of summaries; the detail endpoint (GET /api/fit/sessions/:id)
// returns one summary + the gzipped-then-decoded HR/pace series.
//
// Series on the wire are snake_case to match the rest of the API — see the
// schema note in the worker file.

export const HrZonesSnapshotSchema = z.object({
  z1: z.tuple([z.number(), z.number()]),
  z2: z.tuple([z.number(), z.number()]),
  z3: z.tuple([z.number(), z.number()]),
  z4: z.tuple([z.number(), z.number()]),
  z5: z.tuple([z.number(), z.number()]),
});
export type HrZonesSnapshot = z.infer<typeof HrZonesSnapshotSchema>;

export const SeriesHrSchema = z.object({
  ts_offsets_s: z.array(z.number()),
  hr_bpm: z.array(z.number()),
});
export type SeriesHr = z.infer<typeof SeriesHrSchema>;

export const SeriesPaceSchema = z.object({
  ts_offsets_s: z.array(z.number()),
  speed_mps: z.array(z.number()),
});
export type SeriesPace = z.infer<typeof SeriesPaceSchema>;

export const SeriesElevationSchema = z.object({
  ts_offsets_s: z.array(z.number()),
  altitude_m: z.array(z.number()),
});
export type SeriesElevation = z.infer<typeof SeriesElevationSchema>;

export const CardioSessionSummarySchema = z.object({
  id: z.number(),
  content_hash: z.string(),
  kind: FitCardioKindSchema,
  workout_type: z.string().nullable(),
  recorded_at: z.number(),
  recorded_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  duration_s: z.number().nullable(),
  distance_m: z.number().nullable(),
  avg_hr_bpm: z.number().nullable(),
  max_hr_bpm: z.number().nullable(),
  calories_kcal: z.number().nullable(),
  hr_zones_snapshot: HrZonesSnapshotSchema.nullable(),
  time_above_z3_s: z.number().nullable(),
  pct_in_prescribed_zone: z.number().nullable(),
  pace_at_zone_s_per_km: z.number().nullable(),
  total_ascent_m: z.number().nullable(),
  total_descent_m: z.number().nullable(),
  workout_log_match: z
    .object({
      // Per CLAUDE.md, workout_log.date is ISO YYYY-MM-DD. The regex
      // pins the format at parse time so a malformed value from the
      // wire surfaces as a Zod validation error rather than corrupting
      // downstream week-bucket math.
      date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
      type: z.string(),
    })
    .nullable(),
});
export type CardioSessionSummary = z.infer<typeof CardioSessionSummarySchema>;
export const CardioSessionSummariesSchema = z.array(CardioSessionSummarySchema);

// 0028 — zone-discipline match for run sessions.
const RunZoneSchema = z.enum(['Z1', 'Z2', 'Z3', 'Z4', 'Z5']);
// Exactly one of duration_min/duration_s per segment on the write side
// (seconds granularity carries sub-minute work — strides); the read schema
// stays permissive so old flat-minute blobs and new seconds blobs both parse.
export const RunSegmentSchema = z.object({
  zone: RunZoneSchema,
  duration_min: z.number().optional(),
  duration_s: z.number().optional(),
  reps: z.number().optional(),
  recovery_min: z.number().optional(),
  recovery_s: z.number().optional(),
  recovery_zone: RunZoneSchema.optional(),
});
export type RunSegment = z.infer<typeof RunSegmentSchema>;

/** "20″" for sub-minute seconds, "2′" when whole minutes (either field). */
function fmtSegDuration(min: number | undefined, s: number | undefined): string {
  if (min !== undefined) return `${min}′`;
  const secs = s ?? 0;
  return secs % 60 === 0 ? `${secs / 60}′` : `${secs}″`;
}

/** Compact one-line segment label, e.g. "10′ Z2 · 4×3′ Z3 (2′ Z1) · 10′ Z2"
 *  or "32′ Z2 · 5×20″ Z3 (1′ Z1)" for seconds-granularity strides.
 *  Shared by formatRunTarget (day-data) + the run card so the structure reads
 *  identically across surfaces. */
export function formatRunSegments(segments: RunSegment[]): string {
  return segments
    .map((s) => {
      const dur = fmtSegDuration(s.duration_min, s.duration_s);
      const work = s.reps && s.reps > 1 ? `${s.reps}×${dur} ${s.zone}` : `${dur} ${s.zone}`;
      const recAmount = s.recovery_min ?? s.recovery_s ?? 0;
      const rec =
        recAmount > 0 && s.recovery_zone
          ? ` (${fmtSegDuration(s.recovery_min, s.recovery_s)} ${s.recovery_zone})`
          : '';
      return work + rec;
    })
    .join(' · ');
}

const MinPerZoneSchema = z.object({
  z1: z.number(),
  z2: z.number(),
  z3: z.number(),
  z4: z.number(),
  z5: z.number(),
});
export type MinPerZone = z.infer<typeof MinPerZoneSchema>;
export const PrescribedRunSchema = z.object({
  source: z.enum(['coach', 'generic']),
  zone: RunZoneSchema,
  duration_min: z.number().nullable(),
  target_pace_s_per_km: z.number().nullable(),
  description: z.string().nullable(),
  segments: z.array(RunSegmentSchema).nullable(),
  min_per_zone: MinPerZoneSchema,
});
export type PrescribedRun = z.infer<typeof PrescribedRunSchema>;

export const CardioSessionDetailSchema = CardioSessionSummarySchema.extend({
  hr_series: SeriesHrSchema.nullable(),
  pace_series: SeriesPaceSchema.nullable(),
  elevation_series: SeriesElevationSchema.nullable(),
  // Default null for back-compat with any cached pre-0028 detail responses.
  prescribed_run: PrescribedRunSchema.nullable().default(null),
  time_per_zone_s: MinPerZoneSchema.nullable().default(null),
});
export type CardioSessionDetail = z.infer<typeof CardioSessionDetailSchema>;

export const SyncResponseSchema = z.object({
  synced: z.number(),
  range: z.object({ since: z.string(), until: z.string() }),
  warnings: z.array(
    z.object({
      kind: z.string(),
      training_id: z.number().optional(),
      group_id: z.number().optional(),
      variant_id: z.number().optional(),
      detail: z.string().optional(),
    }),
  ),
  // Default to [] so older servers without the engine field still parse.
  engine_verdicts: z.array(EngineVerdictSchema).default([]),
});
export type SyncResponse = z.infer<typeof SyncResponseSchema>;

// ---- Plan 3 of 4: coach review surface ----

export const CoachSuggestionDimensionSchema = z.enum([
  'activity',
  'details',
  'macros',
  'plan',
]);
export type CoachSuggestionDimension = z.infer<typeof CoachSuggestionDimensionSchema>;

export const CoachSuggestionStatusSchema = z.enum([
  'pending',
  'applied',
  'dismissed',
  'snoozed',
]);
export type CoachSuggestionStatus = z.infer<typeof CoachSuggestionStatusSchema>;

// `suggestion` field is `unknown` — its shape varies by dimension and is
// validated server-side. The PWA renders defensively (narrows per dimension).
export const CoachLatestSuggestionSchema = z.object({
  id: z.string(),
  // Half of the (coach_call_id, suggestion_id) key. suggestion_id is
  // content-derived and stable across calls, so apply/dismiss/snooze must
  // address the row by call too (see apiClient calls in coach-actions.ts).
  coach_call_id: z.number(),
  dimension: CoachSuggestionDimensionSchema,
  suggestion: z.unknown(),
  status: CoachSuggestionStatusSchema,
  auto_dismissed: z.boolean(),
  decided_at: z.number().nullable(),
  snooze_until: z.string().regex(isoDateRegex).nullable(),
  was_snoozed: z.boolean(),
});
export type CoachLatestSuggestion = z.infer<typeof CoachLatestSuggestionSchema>;

// v2 weekly arc summary — bridges detailed_plan_end_date → plan_target_date.
export const WeeklySummarySchema = z.object({
  week_start_date: z.string().regex(isoDateRegex),
  focus: z.string(),
  category_emphasis: z.string(),
  weight_target_kg: z.number().nullable(),
  intensity: z.number().int().min(1).max(10),
  reason: z.string().optional(),
});
export type WeeklySummary = z.infer<typeof WeeklySummarySchema>;

// v2 run week — free-form name + numeric intensity (no fixed phase enum),
// variable length (up to 52 weeks). Keep in sync with the server-side
// planRunWeekSchema in apps/protocol-api/src/schemas/coach.ts.
export const PlanRunWeekSchema = z.object({
  week_index: z.number().int().min(1).max(52),
  // Monday (ISO) of this arc week — the fixed calendar anchor (added with the
  // accepted-run-plan work). Optional so coach calls produced before the
  // schema change (no week_start_date) still parse; the UI falls back to
  // week_index when absent.
  week_start_date: z.string().regex(isoDateRegex).optional(),
  name: z.string(),
  intensity: z.number().int().min(1).max(10),
  summary: z.string(),
  prescribed_runs: z.array(z.string()),
  target_pace_s_per_km: z.number().nullable(),
  target_long_run_min: z.number().nullable(),
  reason: z.string().optional(),
});
export type PlanRunWeek = z.infer<typeof PlanRunWeekSchema>;

// GET /api/run-plan/active?date= — the ACCEPTED run plan in effect on a date
// (or null when nothing accepted as-of that date). The Day-view arc reads this
// instead of the raw latest proposal.
export const ActiveRunPlanSchema = z
  .object({
    plan_start_date: z.string().regex(isoDateRegex),
    coach_call_id: z.number(),
    plan_run_arc: z.array(PlanRunWeekSchema),
  })
  .nullable();
export type ActiveRunPlan = z.infer<typeof ActiveRunPlanSchema>;

// Coach-applied per-date run target (daily_run_target). The Day/Plan run
// surfaces prefer this over the static RUN_PLAN when present for a date.
export const DailyRunTargetSchema = z.object({
  date: z.string(),
  duration_min: z.number(),
  zone: z.enum(['Z1', 'Z2', 'Z3', 'Z4', 'Z5']),
  target_pace_s_per_km: z.number().nullable(),
  description: z.string().nullable(),
  // 0028 — ordered segments; null for a flat single-zone target. Default null
  // for back-compat with cached pre-0028 responses.
  segments: z.array(RunSegmentSchema).nullable().default(null),
  coach_suggestion_id: z.string().nullable(),
  updated_at: z.number(),
});
export type DailyRunTarget = z.infer<typeof DailyRunTargetSchema>;
export const DailyRunTargetsSchema = z.array(DailyRunTargetSchema);

// Per-date macro target history (0024). The effective target on a date is the
// most recent row with date <= that date (resolved client-side via
// resolveEffectiveTarget in day-data.ts).
export const DailyTargetHistorySchema = z.object({
  date: z.string(),
  goal_cal: z.number(),
  goal_protein_g: z.number(),
  goal_fat_g: z.number(),
  goal_carb_g: z.number(),
  source: z.string().nullable(),
  updated_at: z.number(),
});
export type DailyTargetHistory = z.infer<typeof DailyTargetHistorySchema>;
export const DailyTargetHistoryListSchema = z.array(DailyTargetHistorySchema);

// GET /api/data-health — freshness is about evidence availability, not a
// judgement of the operator's health.
export const DataHealthSourceStatusSchema = z.enum([
  'current', 'delayed', 'stale', 'missing', 'error', 'idle', 'action_required',
]);
export const DataHealthSourceSchema = z.object({
  key: z.enum(['weight', 'nutrition', 'strength', 'speediance', 'running', 'apple_health', 'targets']),
  label: z.string(),
  status: DataHealthSourceStatusSchema,
  last_recorded_on: z.string().nullable(),
  last_updated_at: z.number().nullable(),
  provenance: z.string(),
  detail: z.string(),
  action: z.string().nullable(),
});
export const CoachingDependencySchema = z.object({
  key: z.enum(['weight_trend', 'nutrition_coaching', 'strength_coaching', 'run_coaching']),
  ready: z.boolean(),
  status: z.enum(['ready', 'limited', 'paused']).optional(),
  detail: z.string(),
  requires: z.array(z.string()).default([]),
});
export const DataHealthResponseSchema = z.object({
  status: z.enum(['healthy', 'attention', 'limited']),
  generated_at: z.number(),
  summary: z.string(),
  sources: z.array(DataHealthSourceSchema),
  dependencies: z.array(CoachingDependencySchema),
  effective_target: DailyTargetHistorySchema,
  nutrition_plan: z.object({
    valid: z.boolean(),
    macro_energy_kcal: z.number(),
    difference_kcal: z.number(),
    detail: z.string(),
  }),
});
export type DataHealthResponse = z.infer<typeof DataHealthResponseSchema>;
export type CoachingDependency = z.infer<typeof CoachingDependencySchema>;

// Warn-tier rail finding persisted on an ACCEPTED coach call (server
// rails.ts). suggestion_index, when present, indexes the response's
// 'details'-dimension suggestions (materialisation preserves
// details_per_activity order).
export const CoachWarningSchema = z.object({
  rail: z.string(),
  detail: z.string(),
  suggestion_index: z.number().optional(),
});
export type CoachWarning = z.infer<typeof CoachWarningSchema>;

// The coach's brief: a headline and three short lists. Calls made before the
// brief existed have none and fall back to `narrative` prose.
export const CoachBriefSchema = z.object({
  headline: z.string(),
  changed: z.array(z.string()),
  watch_for: z.array(z.string()),
  your_moves: z.array(z.string()),
});
export type CoachBrief = z.infer<typeof CoachBriefSchema>;

export const CoachLatestResponseSchema = z.object({
  coach_call: z.object({
    id: z.number(),
    date_lisbon: z.string(),
    trigger: z.string(),
    narrative: z.string().nullable(),
    // Defaulted so a Worker that predates the brief still parses.
    brief: CoachBriefSchema.nullable().default(null),
    model: z.string(),
    retries: z.number(),
  }),
  // Default for backward-compat with any pre-v2 ok rows that may still
  // be in the DB at deploy time. New v2 rows always have these.
  plan_target_date: z.string().default(''),
  detailed_plan_end_date: z.string().default(''),
  suggestions: z.array(CoachLatestSuggestionSchema),
  plan_run_arc: z.array(PlanRunWeekSchema).default([]),
  arc_weekly_summaries: z.array(WeeklySummarySchema).default([]),
  warnings: z.array(CoachWarningSchema).default([]),
  data_health: z.object({
    dependencies: z.array(CoachingDependencySchema),
    nutrition_validation: z.object({
      status: z.enum(['validated', 'paused', 'unavailable']),
      detail: z.string(),
      checks: z.array(z.string()),
    }),
  }).default({
    dependencies: [],
    nutrition_validation: {
      status: 'unavailable' as const,
      detail: 'This coach call predates nutrition readiness metadata.',
      checks: [],
    },
  }),
});
export type CoachLatestResponse = z.infer<typeof CoachLatestResponseSchema>;

export const CoachActionResponseSchema = z.object({
  // Mirror snooze_until + decided_at validation from the latest schema —
  // both surfaces talk to the same column.
  id: z.string(),
  dimension: CoachSuggestionDimensionSchema,
  status: CoachSuggestionStatusSchema,
  auto_dismissed: z.boolean(),
  decided_at: z.number().nullable(),
  snooze_until: z.string().regex(isoDateRegex).nullable(),
});
export type CoachActionResponse = z.infer<typeof CoachActionResponseSchema>;

// ---- Coach adjustments: re-plan a few days around an availability change ----

// 'no_change' is still a recorded call: the brief explains why the plan
// already fits, and there is nothing to apply.
export const CoachAdjustResponseSchema = z.object({
  status: z.enum(['ok', 'no_change']),
  coach_call_id: z.number(),
  // Written before the model runs, so the next weekly rebuild respects the
  // change even when this proposal is discarded or the call fails.
  constraint_window_id: z.number(),
  changed_dates: z.array(z.string().regex(isoDateRegex)),
  brief: CoachBriefSchema,
});
export type CoachAdjustResponse = z.infer<typeof CoachAdjustResponseSchema>;

export const CoachAdjustOpenResponseSchema = z.object({
  coach_call_id: z.number().nullable(),
});
export type CoachAdjustOpenResponse = z.infer<typeof CoachAdjustOpenResponseSchema>;

export const CoachAdjustDiscardResponseSchema = z.object({
  dismissed: z.number(),
});
export type CoachAdjustDiscardResponse = z.infer<typeof CoachAdjustDiscardResponseSchema>;

// ---- Plan 3 redesign: list endpoint + run arc ----

export const CoachCallSummarySchema = z.object({
  id: z.number(),
  date_lisbon: z.string(),
  trigger: z.string(),
  // Defaulted so a Worker that predates adjustments still parses.
  kind: z.literal('adjustment').nullable().default(null),
  model: z.string(),
  started_at: z.number(),
  retries: z.number(),
  input_tokens: z.number().nullable(),
  output_tokens: z.number().nullable(),
  counts: z.object({
    total: z.number(),
    pending: z.number(),
    applied: z.number(),
    dismissed: z.number(),
    snoozed: z.number(),
  }),
});
export type CoachCallSummary = z.infer<typeof CoachCallSummarySchema>;

export const CoachCallListResponseSchema = z.object({
  calls: z.array(CoachCallSummarySchema),
});
export type CoachCallListResponse = z.infer<typeof CoachCallListResponseSchema>;

// PlanRunWeekSchema + WeeklySummarySchema are defined above alongside
// CoachLatestResponseSchema (v2 — Plan 3.5 U18).

// GET /api/strength/by-muscle — per-region strength volume trajectory. Each
// region carries a per-week volume series (kg, oldest→newest) for a sparkline
// plus the window total + last-trained date. Mirrors the server's
// StrengthByMuscleResponse (apps/protocol-api/src/routes/strength-muscle.ts).
export const RegionTrajectorySchema = z.object({
  region: z.enum(['Push', 'Pull', 'Legs', 'Core', 'Full Body']),
  weekly: z.array(z.number()),
  total_kg: z.number(),
  last_trained_date: z.string().regex(isoDateRegex).nullable(),
});
export type RegionTrajectory = z.infer<typeof RegionTrajectorySchema>;
// Bodyweight strength reps series. Bodyweight days (all prescribed sets at
// weight_kg=0) carry no kg tonnage; they're tracked by reps instead, kept
// separate from the kg region trend. null when no completed bodyweight days
// exist in the window. Mirrors the server's BodyweightTrajectory.
export const BodyweightTrajectorySchema = z.object({
  weekly: z.array(z.number()),
  total_reps: z.number(),
  last_trained_date: z.string().regex(isoDateRegex).nullable(),
});
export type BodyweightTrajectory = z.infer<typeof BodyweightTrajectorySchema>;
export const StrengthByMuscleResponseSchema = z.object({
  regions: z.array(RegionTrajectorySchema),
  bodyweight: BodyweightTrajectorySchema.nullable().optional(),
});
export type StrengthByMuscleResponse = z.infer<typeof StrengthByMuscleResponseSchema>;
